import { useState, useEffect } from 'react';
import FundingSearchGuy from 'assets/svgs/businessMenu/new-funding-search-guy.svg';
import FundingSearchGuyMobile from 'assets/svgs/businessMenu/new-funding-search-guy-mobile.svg';

import useQuestionaireContext from 'modules/Business/hooks/useQuestionaireContext';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { useFeatureFlagContext } from 'hooks/useFeatureFlags';
import GrowingGuy from 'assets/svgs/businessMenu/growing-guy.svg';
import GrowingGuyMobile from 'assets/svgs/businessMenu/growing-guy-mobile.svg';
import { PaymentOrigin, useRedirectTrackingPaymentOrigin } from '@capitalise/common';

export const OPTIONS = Object.freeze({
  FUNDING_OPTIONS: {
    title: 'Start a funding search with 100+ lenders',
    baseColor: 'green',
    image: <FundingSearchGuy />,
    imageMobile: <FundingSearchGuyMobile />,
    analytic: {
      category: AnalyticsEventCategory.business,
      subCategory: AnalyticsEventAction.launchFundingSearch,
    },
  },
  UPGRADE_OPTIONS: {
    title: 'Get Pro and check other companies credit scores',
    baseColor: 'blue',
    image: <GrowingGuy />,
    imageMobile: <GrowingGuyMobile />,
    analytic: {
      category: AnalyticsEventCategory.business,
      subCategory: AnalyticsEventAction.upgradeStart,
      extraParams: {
        upgradeOrigin: 'navigation card',
      },
    },
  },
  CREDIT_PROFILE_OPTIONS: {
    title: 'Upgrade to access your full Experian credit file',
    baseColor: 'blue',
    image: <GrowingGuy />,
    imageMobile: <GrowingGuyMobile />,
    analytic: {
      category: AnalyticsEventCategory.business,
      subCategory: AnalyticsEventAction.upgradeStart,
      extraParams: {
        upgradeOrigin: 'navigation card',
      },
    },
  },
});

const useSideBarBanner = (pathName, isPro, shouldDisplayCreditProfile) => {
  const [banner, setBanner] = useState(null);
  const { isOpen, setIsOpen } = useQuestionaireContext();
  const flags = useFeatureFlagContext();
  const hasFeatureFunding = flags?.feature_funding === true;
  const { redirectToPaymentFlow } = useRedirectTrackingPaymentOrigin();

  const sidebarOptions = {
    '/business/today':
      !isPro && shouldDisplayCreditProfile
        ? {
            ...OPTIONS.CREDIT_PROFILE_OPTIONS,
            bannerCallback: () => {
              return redirectToPaymentFlow(PaymentOrigin.CREDIT_PROFILE);
            },
          }
        : hasFeatureFunding
        ? { ...OPTIONS.FUNDING_OPTIONS, bannerCallback: () => setIsOpen(!isOpen) }
        : {},
    '/business/monitor': !isPro
      ? {
          ...OPTIONS.UPGRADE_OPTIONS,
          bannerCallback: () => {
            return redirectToPaymentFlow(PaymentOrigin.MONITOR);
          },
        }
      : hasFeatureFunding
      ? { ...OPTIONS.FUNDING_OPTIONS, bannerCallback: () => setIsOpen(!isOpen) }
      : {},
    '/business/applications': hasFeatureFunding
      ? {
          ...OPTIONS.FUNDING_OPTIONS,
          bannerCallback: () => setIsOpen(!isOpen),
        }
      : {},
    '/business/funding': hasFeatureFunding
      ? {
          ...OPTIONS.FUNDING_OPTIONS,
          bannerCallback: () => setIsOpen(!isOpen),
        }
      : {},
    '/business/capital-report': hasFeatureFunding
      ? {
          ...OPTIONS.FUNDING_OPTIONS,
          bannerCallback: () => setIsOpen(!isOpen),
        }
      : {},
    '/business/credit-profile':
      !isPro && shouldDisplayCreditProfile
        ? {
            ...OPTIONS.CREDIT_PROFILE_OPTIONS,
            bannerCallback: () => {
              return redirectToPaymentFlow(PaymentOrigin.CREDIT_PROFILE);
            },
          }
        : hasFeatureFunding
        ? { ...OPTIONS.FUNDING_OPTIONS, bannerCallback: () => setIsOpen(!isOpen) }
        : null,
  };

  useEffect(() => {
    setBanner(sidebarOptions[pathName]);
  }, [isPro, shouldDisplayCreditProfile]);

  return banner;
};

export default useSideBarBanner;
